// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import {
  PitbullProvinces,
  Step,
  Option,
  StepOnePF,
  GenderInitials,
  IdentityType,
  IdentityVersion,
  PersonType,
} from "../../types";
import { RootState } from "../../app/store";

// UTILS
import {
  capitalizeString,
  resetFields,
  parseDate,
  parseGenderFromInitial,
  completeAddress,
  handleSendStep,
} from "../../app/utils";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { civilStatusOptions, parseStateAddress, pepOptions } from "../../utils";
import { getDraftReferrer } from "../../utils/draftBase";
import {
  cuitSpouse,
  largeStringRequired,
  mediumSelectedAddressRequired,
  phone,
  radioOrSingleSelectRequired,
  smallString,
  smallStringRequired,
  zipCodeRequired,
} from "../../validations";

const personalInformationStep = (
  countriesOptions: Option[],
  citizenshipOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
  onSendStep?: (
    formValues: FormikValues | null,
    step: Record<string, StepOnePF>,
  ) => Promise<void>,
): Omit<Step, "step"> => ({
  label: "personalInformation",
  name: "Datos personales",
  disableButtonBack: true,
  subSteps: [
    {
      step: 1,
      mobileName: "Datos básicos",
      fields: [
        {
          name: "name",
          type: "text",
          label: "Nombre/s",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          disabled: true,
        },
        {
          name: "lastName",
          type: "text",
          label: "Apellido/s",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          disabled: true,
          formatInPreConfirmation: (value) => capitalizeString(value),
        },
        {
          name: "citizenship",
          type: "select",
          label: "Nacionalidad",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          options: citizenshipOptions,
          disabled: (state) => !!state?.onboardingData?.formValues?.citizenship,
          onChange: (_values, setFieldValue, _, _setFieldTouched, value) => {
            setFieldValue("citizenship", value);
          },
        },
        {
          name: "birthDate",
          type: "date",
          label: "Fecha de nacimiento",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          disabled: true,
        },
        {
          name: "civilStatus",
          type: "select",
          label: "Estado civil",
          placeholder: "Seleccioná una opción",
          validation: radioOrSingleSelectRequired(civilStatusOptions),
          options: civilStatusOptions,
          selectType: "single",
          initialValue: "",
          columns: 2,
        },
        {
          name: "spouseName",
          type: "text",
          label: "Nombre/s del cónyuge",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          conditional: {
            name: "civilStatus",
            value: "MARRIED",
          },
        },
        {
          name: "spouseLastName",
          type: "text",
          label: "Apellido/s del cónyuge",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          conditional: {
            name: "civilStatus",
            value: "MARRIED",
          },
        },
        {
          name: "spouseCuil",
          type: "cuit",
          label: "CUIL del cónyuge",
          validation: cuitSpouse,
          initialValue: "",
          placeholder: "XX-XXXXXXXX-X",
          conditional: {
            name: "civilStatus",
            value: "MARRIED",
          },
          showOptionalLabel: true,
          columns: 2,
        },
        {
          name: "pep",
          type: "select",
          label: "¿Estás expuesto/a políticamente?",
          tooltip: {
            label: "¿Qué significa?",
            modal: "modal-PEP",
          },
          placeholder: "Seleccioná una opción",
          validation: radioOrSingleSelectRequired(pepOptions),
          options: pepOptions,
          selectType: "single",
          initialValue: "",
          preconfirmationLabel: "¿Expuesto/a políticamente?",
          columns: 2,
          startsRow: true,
        },
        {
          conditional: {
            name: "pep",
            value: ["DIRECT", "FAMILY"],
          },
          name: "pepDetails",
          type: "text",
          label: "Describí brevemente la situación",
          validation: largeStringRequired,
          initialValue: "",
          columns: 2,
        },
        {
          name: "telephone",
          type: "phone",
          label: "Teléfono celular",
          validation: phone,
          initialValue: "",
          inputMode: "numeric",
          infoText: (value: string | undefined) => {
            if (!value || value.replace(/^\+/, "").startsWith("54"))
              return "Con código de área sin 0 ni 15.";
          },
          formatInPreConfirmation: (value) =>
            value && value.startsWith("+") ? value : `+${value}`,
        },
      ],
    },
    {
      step: 2,
      mobileName: "Dirección",
      fields: [
        {
          name: fullAddressFields["country"],
          type: "select",
          label: "País de residencia",
          placeholder: "Seleccioná una opción",
          validation: radioOrSingleSelectRequired(countriesOptions),
          options: countriesOptions,
          selectType: "single",
          initialValue: "",
          onChange: (_, setFieldValue) =>
            resetFields(addressFieldsNames, setFieldValue),
        },
        {
          name: fullAddressFields["fullAddress"],
          type: "address",
          label: "Domicilio",
          validation: mediumSelectedAddressRequired,
          initialValue: "",
          autoSelect: fullAddressFields["fullAddress"],
          country: fullAddressFields["country"],
          onChange: (values, setFieldValue) => {
            const {
              formattedAddress: {
                postalCode,
                state,
                city,
                municipality,
                street,
                streetNumber,
                country,
              },
            } = values;

            const address = {
              country,
              state,
              city,
              municipality,
              street,
              number: streetNumber,
              zipCode: postalCode,
            };

            return completeAddress(
              addressFields,
              address,
              setFieldValue,
              getProvincesFilteredByCountry,
            );
          },
          autoComplete: "off",
          formatInPreConfirmation: (value) => value?.split(",")[0], // Only show street
        },
        {
          name: fullAddressFields["floor"],
          type: "text",
          label: "Piso",
          validation: smallString,
          initialValue: "",
          columns: 2,
          maxLength: 10,
        },
        {
          name: fullAddressFields["apartment"],
          type: "text",
          label: "Departamento",
          validation: smallString,
          initialValue: "",
          columns: 2,
          maxLength: 10,
        },
        {
          name: fullAddressFields["zipCode"],
          type: "text",
          label: "Código postal",
          validation: zipCodeRequired,
          initialValue: "",
          columns: 1,
        },
        {
          name: fullAddressFields["municipality"],
          type: "text",
          label: "Localidad",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
        },
        {
          name: fullAddressFields["state"],
          type: "select",
          label: "Provincia",
          validation: radioOrSingleSelectRequired(
            getProvincesOptions(provinces),
          ),
          searchable: true,
          initialValue: "",
          dynamicOptions: (formik) => {
            const country = formik.values.addresses.country;
            const countryProvinces = getProvincesFilteredByCountry(country);
            return getProvincesOptions(countryProvinces);
          },
          columns: 2,
        },
      ],
      onContinue: async (values: FormikValues, dispatch, state) => {
        if (!state || !dispatch)
          throw new Error("Error: state or dispatch not found");

        const { sendedSteps, formValues } = state.onboardingData;

        const step = getParsedStepValues(values, state, formValues);
        if (onSendStep) {
          await onSendStep(formValues, { stepOne: step });
        } else {
          await handleSendStep({
            dispatch,
            personType: "PHYSICAL" as PersonType,
            email: values.email || formValues?.email,
            sendedSteps,
            stepId: 1,
            step,
          });
        }

        logAnalyticsAndHotjarEvent("step_personal_data_completed_pf");
      },
    },
  ],
});

const addressFields = {
  fullAddress: "addresses.fullAddress",
  municipality: "addresses.municipality",
  state: "addresses.state",
  zipCode: "addresses.zipCode",
  city: "addresses.city",
  street: "addresses.street",
  apartment: "addresses.apartment",
  number: "addresses.number",
  floor: "addresses.floor",
};

const fullAddressFields = {
  country: "addresses.country",
  ...addressFields,
};

const addressFieldsNames = Object.values(addressFields);

const getProvincesOptions = (provinces: PitbullProvinces) =>
  provinces.map((prov) => ({
    value: prov.name,
    label: prov.name,
  }));

const getParsedStepValues = (
  values: FormikValues,
  state: RootState,
  formValues: FormikValues | null,
): StepOnePF => {
  const {
    name,
    lastName,
    cuit,
    email,
    gender,
    identityDocument,
    tramitNumber,
    citizenship,
    civilStatus,
    telephone,
    birthDate,
    birthCountry,
    pep,
    addresses: {
      floor,
      apartment,
      state: stateAddress,
      city,
      street,
      number,
      municipality,
      country,
      zipCode,
    },
    spouseCuil,
    spouseLastName,
    spouseName,
    accountIdToLink,
  } = values;

  const stepOne =
    (state.onboardingData?.sendedSteps?.find(
      (step) => "personalInformation" in step,
    ) as StepOnePF) ||
    (state.onboardingData?.additionalSentSteps?.stepOne as StepOnePF);

  const versionOfIdentity =
    state.renaper.copy || stepOne?.personalInformation.identityDocument.version;

  const creationDate = state.renaper.creationDate
    ? parseDate(state.renaper.creationDate)
    : stepOne?.personalInformation.identityDocument.creationDate;

  const expirationDate = state.renaper.expirationDate
    ? parseDate(state.renaper.expirationDate)
    : stepOne?.personalInformation.identityDocument.expirationDate;

  const procedureId = state.renaper.procedureId || tramitNumber;

  const parsedBirthDate = parseDate(birthDate);

  return {
    "@type": "1_PHYSICAL",
    personalInformation: {
      name,
      lastName,
      cuit,
      email: email || formValues?.email,
      gender: parseGenderFromInitial[gender as GenderInitials],
      identityDocument: {
        type: "DNI" as IdentityType,
        idNumber: identityDocument,
        procedureId,
        version: versionOfIdentity as IdentityVersion,
        creationDate,
        expirationDate,
      },
      telephone,
      citizenship,
      birthCountry,
      birthDate: parsedBirthDate,
      civilStatus,
      pep,
      pepDetails: pep === "NONE" ? undefined : values.pepDetails,
      addresses: [
        {
          street,
          number,
          zipCode,
          city,
          state: parseStateAddress(stateAddress) || "",
          country,
          floor,
          apartment,
          municipality,
        },
      ],
      spouse:
        civilStatus === "MARRIED"
          ? {
              ...(spouseCuil && { cuit: spouseCuil }),
              lastName: spouseLastName,
              name: spouseName,
            }
          : undefined,
    },
    referrer: getDraftReferrer(values),
    accountIdToLink,
  };
};

export default personalInformationStep;
